const API_BASE = import.meta.env['VITE_API_URL'] ?? 'http://localhost:3000'

export function getApiBase(): string {
  return API_BASE.replace(/\/$/, '')
}

export function getAuthToken(): string | null {
  return localStorage.getItem('datamatch_token')
}

export function setAuthToken(token: string): void {
  localStorage.setItem('datamatch_token', token)
}

export function clearAuthToken(): void {
  localStorage.removeItem('datamatch_token')
}

export function authFetch(
  url: string,
  options: RequestInit = {}
): Promise<Response> {
  const token = getAuthToken()
  const headers = new Headers(options.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  return fetch(url, { ...options, headers })
}

// ---- Batch dashboard API helpers ----

export interface BatchSummary {
  id: number
  batchName: string
  fileName: string
  createdAt: string
  startedAt: string | null
  completedAt: string | null
  status:
    | 'created'
    | 'queued'
    | 'processing'
    | 'completed'
    | 'failed'
    | 'stopped'
  queuePosition: number | null
  totalProducts: number
  matchedProducts: number
  processedCount?: number
  failedCount?: number
  coverage: number
  progress: number
  estimatedCompletion: string | null
}

export interface BatchPagination {
  currentPage: number
  totalPages: number
  totalBatches: number
  batchesPerPage: number
}

export interface BatchesResponse {
  batches: BatchSummary[]
  pagination: BatchPagination
}

export interface BatchMetrics {
  totalBatches: number
  totalProducts: number
  totalMatched: number
  overallCoverage: number
  trends?: {
    coverageChange?: number
    batchesChange?: number
  }
}

export type BatchStatusFilter =
  | 'all'
  | 'created'
  | 'queued'
  | 'processing'
  | 'completed'
  | 'failed'
  | 'stopped'

export async function fetchBatches(
  params: {
    page?: number
    limit?: number
    status?: BatchStatusFilter
    sortBy?: 'id' | 'createdAt'
    sortOrder?: 'asc' | 'desc'
  } = {}
): Promise<BatchesResponse> {
  const base = getApiBase()
  const url = new URL(`${base}/api/products/batches`)
  if (params.page != null) url.searchParams.set('page', String(params.page))
  if (params.limit != null) url.searchParams.set('limit', String(params.limit))
  if (params.status && params.status !== 'all') {
    url.searchParams.set('status', params.status)
  }
  if (params.sortBy) url.searchParams.set('sortBy', params.sortBy)
  if (params.sortOrder) url.searchParams.set('sortOrder', params.sortOrder)

  const res = await authFetch(url.toString())
  if (!res.ok) {
    throw new Error('Failed to fetch batches')
  }
  const json = (await res.json()) as BatchesResponse
  return json
}

export async function fetchBatchById(batchId: number): Promise<
  BatchSummary & {
    timeline?: {
      created: string
      started: string | null
      completed: string | null
      estimatedCompletion: string | null
    }
  }
> {
  const base = getApiBase()
  const res = await authFetch(`${base}/api/products/batches/${batchId}`)
  if (!res.ok) {
    throw new Error('Failed to fetch batch')
  }
  return (await res.json()) as BatchSummary & {
    timeline?: {
      created: string
      started: string | null
      completed: string | null
      estimatedCompletion: string | null
    }
  }
}

export async function fetchBatchMetrics(): Promise<BatchMetrics> {
  const base = getApiBase()
  const res = await authFetch(`${base}/api/products/metrics`)
  if (!res.ok) {
    throw new Error('Failed to fetch batch metrics')
  }
  return (await res.json()) as BatchMetrics
}

/** Response from GET /api/products/status (matching status + cooldown). */
export interface MatchingStatusResponse {
  status: 'idle' | 'running'
  batches: Array<{
    batchId: number
    status: string
    total: number
    processedCount: number
    matchedCount: number
    /** True if this run was started via "Re-run failed"; used to show correct button after Stop. */
    retryFailedOnly?: boolean
  }>
  /** Seconds remaining in cooldown; 0 or omitted when not in cooldown. */
  cooldownRemainingSeconds?: number
  /** ISO timestamp when cooldown ends. */
  cooldownUntil?: string
}

export interface ExtendedMatchingStatusResponse extends MatchingStatusResponse {
  metrics: BatchMetrics
  batchesList?: BatchesResponse
}

export interface MatchingStatusParams {
  page?: number
  limit?: number
  status?: BatchStatusFilter
  sortBy?: 'id' | 'createdAt'
  sortOrder?: 'asc' | 'desc'
}

export function subscribeToMatchingStatus(
  params: MatchingStatusParams,
  onMessage: (data: ExtendedMatchingStatusResponse) => void
): () => void {
  const base = getApiBase()
  const url = new URL(`${base}/api/products/status/stream`)

  if (params.page != null) url.searchParams.set('page', String(params.page))
  if (params.limit != null) url.searchParams.set('limit', String(params.limit))
  if (params.status && params.status !== 'all') {
    url.searchParams.set('status', params.status)
  }
  if (params.sortBy) url.searchParams.set('sortBy', params.sortBy)
  if (params.sortOrder) url.searchParams.set('sortOrder', params.sortOrder)

  // Pass token in query param or rely on cookie/window logic?
  // EventSource doesn't support custom headers.
  // We'll append the token to the URL as a query param 'token'.
  const token = getAuthToken()
  if (token) {
    url.searchParams.set('token', token)
  }

  const eventSource = new EventSource(url.toString())

  eventSource.onmessage = event => {
    try {
      const data = JSON.parse(event.data) as ExtendedMatchingStatusResponse
      onMessage(data)
    } catch {
      // ignore parse error
    }
  }

  return () => {
    eventSource.close()
  }
}

export async function fetchMatchingStatus(
  params?: MatchingStatusParams
): Promise<ExtendedMatchingStatusResponse> {
  const base = getApiBase()
  const url = new URL(`${base}/api/products/status`)

  if (params) {
    if (params.page != null) url.searchParams.set('page', String(params.page))
    if (params.limit != null)
      url.searchParams.set('limit', String(params.limit))
    if (params.status && params.status !== 'all') {
      url.searchParams.set('status', params.status)
    }
    if (params.sortBy) url.searchParams.set('sortBy', params.sortBy)
    if (params.sortOrder) url.searchParams.set('sortOrder', params.sortOrder)
  }

  const res = await authFetch(url.toString())
  if (!res.ok) {
    throw new Error('Failed to fetch matching status')
  }
  return (await res.json()) as ExtendedMatchingStatusResponse
}
